import React, { useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useServiceAuth from '@lifeomic/phc-web-toolkit/src/hooks/useServiceAuth';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks';
import { PulseLoader } from '../../../common/PulseLoader';
import { MedicationViewModal, type MedicationResource } from './Medications';
import { ConditionViewModal } from './Conditions';
import { AllergyViewModal } from './Allergies';
import { ProcedureViewModal } from './Procedures';
import {
  ImmunizationViewModal,
  type ImmunizationResource,
} from './Immunizations';
import RangeViewModalV2 from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Measurements/Graphs/Ranges/RangeViewModalV2';
import type {
  ParsedCitationReference,
  ParsedFhirCitation,
  ParsedMedicalResultCitation,
} from './citationReference';

// Re-exported so existing importers can keep importing parsing from here; the
// definitions live in `./citationReference` (no heavy UI imports) so routing
// and tests can use them without pulling in the range-view chart stack.
export {
  CITATION_RESOURCE_TYPES,
  parseCitationReference,
} from './citationReference';
export type {
  CitationResourceType,
  ParsedCitationReference,
  ParsedFhirCitation,
  ParsedMedicalResultCitation,
} from './citationReference';

const messages = defineMessages({
  loadingTitle: {
    id: 'CitationResourceModal.loadingTitle',
    defaultMessage: 'Loading…',
  },
  errorTitle: {
    id: 'CitationResourceModal.errorTitle',
    defaultMessage: 'Unable to load citation',
  },
  errorBody: {
    id: 'CitationResourceModal.errorBody',
    defaultMessage: 'This referenced record could not be loaded.',
  },
});

export type CitationResourceModalProps = {
  reference: ParsedCitationReference;
  subjectId: string;
  onDismiss: () => void;
};

/**
 * Opens a citation's referenced record in the appropriate modal: lab/biomarker
 * citations use the shared range view (matching clinician chat), and everything
 * else fetches the FHIR resource and renders the member-detail data-view modal.
 * Mount this component keyed by the reference so each variant binds cleanly.
 */
export const CitationResourceModal: React.FC<CitationResourceModalProps> = ({
  reference,
  subjectId,
  onDismiss,
}) => {
  if (reference.kind === 'medicalResult') {
    return (
      <MedicalResultCitationModal
        reference={reference}
        subjectId={subjectId}
        onDismiss={onDismiss}
      />
    );
  }
  return (
    <FhirCitationModal
      reference={reference}
      subjectId={subjectId}
      onDismiss={onDismiss}
    />
  );
};

/**
 * Opens a lab/biomarker citation in the shared range view, the same modal
 * clinician chat uses for "medical result" citations. `RangeViewModalV2`
 * queries the result history by system + code and selects the cited result.
 */
const MedicalResultCitationModal: React.FC<{
  reference: ParsedMedicalResultCitation;
  subjectId: string;
  onDismiss: () => void;
}> = ({ reference, subjectId, onDismiss }) => {
  const patient = useMemo(
    () => ({ resourceType: 'Patient', id: subjectId } as fhir.Patient),
    [subjectId],
  );
  return (
    <RangeViewModalV2
      isOpen
      onClose={onDismiss}
      patient={patient}
      dimension={{
        codes: [{ system: reference.system, key: reference.code }],
      }}
      initialSelectedResourceId={reference.resourceId}
    />
  );
};

/**
 * Fetches a citation's referenced FHIR resource and renders the same detail
 * modal used by the corresponding member-detail data view (Medications,
 * Conditions, etc.). Mount this component keyed by the reference so the
 * underlying `useService` binds to the correct resource type.
 */
const FhirCitationModal: React.FC<{
  reference: ParsedFhirCitation;
  subjectId: string;
  onDismiss: () => void;
}> = ({ reference, subjectId, onDismiss }) => {
  const { formatMessage } = useIntl();
  const accountId = useActiveAccountId();
  const auth = useServiceAuth();
  const service = useMemo(
    () => new FhirService(reference.resourceType),
    [reference.resourceType],
  );
  const [fetchById, state] = useService(service.getById);

  useEffect(() => {
    if (accountId && auth) {
      void fetchById(accountId, reference.id);
    }
  }, [accountId, auth, fetchById, reference.id]);

  const resource = state.response?.data ?? null;

  if (state.isFetching || (!resource && !state.error)) {
    return (
      <Modal
        isOpen
        size={1}
        fullWidth
        onDismiss={onDismiss}
        title={formatMessage(messages.loadingTitle)}
        data-testid="citation-resource-loading"
      >
        <Box padding={4} align="center" justify="center">
          <PulseLoader width={96} />
        </Box>
      </Modal>
    );
  }

  if (!resource) {
    return (
      <Modal
        isOpen
        size={1}
        fullWidth
        onDismiss={onDismiss}
        title={formatMessage(messages.errorTitle)}
        data-testid="citation-resource-error"
      >
        <Box padding={2}>
          <Text size="subbody">{formatMessage(messages.errorBody)}</Text>
        </Box>
      </Modal>
    );
  }

  switch (reference.resourceType) {
    case 'MedicationRequest':
    case 'MedicationStatement':
      return (
        <MedicationViewModal
          resource={resource as MedicationResource}
          practitionerNames={{}}
          subjectId={subjectId}
          onDismiss={onDismiss}
          onRejected={onDismiss}
        />
      );
    case 'Condition':
      return (
        <ConditionViewModal
          resource={resource as fhir.Condition}
          subjectId={subjectId}
          onDismiss={onDismiss}
          onApproved={onDismiss}
          onRejected={onDismiss}
        />
      );
    case 'AllergyIntolerance':
      return (
        <AllergyViewModal
          resource={resource as fhir.AllergyIntolerance}
          subjectId={subjectId}
          onDismiss={onDismiss}
          onApproved={onDismiss}
          onRejected={onDismiss}
        />
      );
    case 'Procedure':
      return (
        <ProcedureViewModal
          resource={resource as fhir.Procedure}
          subjectId={subjectId}
          onDismiss={onDismiss}
          onRejected={onDismiss}
        />
      );
    case 'Immunization':
      return (
        <ImmunizationViewModal
          resource={resource as ImmunizationResource}
          subjectId={subjectId}
          onDismiss={onDismiss}
          onRejected={onDismiss}
        />
      );
  }
};
